import React, { useState } from 'react'
import { useEffectOnce } from 'react-use'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import NaturalPersonDetails from '../../../../views/components/organisms/FormGroups/NaturalPersonDetails'
import { Button, FormLoader, Heading, Radio } from '../../../../views/components'
import { StatefulOtpVerification } from '../../../user'
import { agencyApiEvents, agencyApiSelectors } from '../../../api/agency'
import styles from './InviteUserForm.module.scss'
import { partyApiSelectors } from '../../../api/party'
import { find, identity, isEmpty, memoizeWith } from 'ramda'
import { apiStateSelectors } from '../../../api/apiState'
import FormErrors from '../../../../views/components/molecules/FormErrors/FormErrors'
import { $TSFixMe } from 'types/ts-migrate'
import { userRoles } from 'modules/api/user/constants'
import { getRequiredFields, hasAllRequiredFields, identityNumberValidation } from 'utils/yup'
import { goBack } from 'connected-react-router'
import RadioGroupCards from 'views/components/molecules/RadioGroupCards/RadioGroupCards'
import InviteUserDetails from 'views/components/organisms/FormGroups/InviteUserDetails'

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('required.')
    .matches(/^[^0-9]*$/, {
      message: 'must be alphabetic.',
    }),
  lastName: yup
    .string()
    .required('required.')
    .matches(/^[^0-9]*$/, {
      message: 'must be alphabetic.',
    }),
  emailAddress: yup.string().required('required.').email('Email is invalid.'),
  alternativeEmailAddress: yup.string().test('alternativeEmailAddress', function (value) {
    if (value && value.length > 0 && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
      return this.createError({
        path: 'alternativeEmailAddress',
        message: 'Email is invalid.',
      })
    } else {
      return true
    }
  }),
})

const validationSchemaWithSaIdNumber = validationSchema.shape({
  idNumber: identityNumberValidation,
})

const validationSchemaWithPassportNumber = validationSchema.shape({
  passport: yup.string().required('required.'),
})

const createButtonContainer = memoizeWith(identity, (selectedPartyId: any, handleReset: () => void) => {
  const ButtonContainer = ({ children }: { children: React.ReactNode }): React.ReactElement => (
    <div className={styles.footer}>
      {children}
      {selectedPartyId && (
        <Button className={styles['button-reset']} size="md" type="button" secondary onClick={handleReset}>
          Reset form
        </Button>
      )}
    </div>
  )
  ButtonContainer.displayName = 'ButtonContainer'
  return ButtonContainer
})

const InviteUserForm = (): React.ReactElement => {
  const dispatch = useDispatch()
  const [otpOpen, setOtpOpen] = useState(false)
  const [formValues, setFormValues] = useState<$TSFixMe>(null)
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null)
  const [otpPin, setOtpPin] = useState<string | null>(null)
  const [stateIdentificationType, setStateIdentificationType] = useState('saIdNumber')
  const [selectedRole, setSelectedRole] = useState<string>(userRoles.TeamMember)

  const isSubmitting = useSelector(state =>
    apiStateSelectors.isLoading(state)([agencyApiEvents.sendInvite_request, agencyApiEvents.sendPartyInvite_request]),
  )

  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  const getValidationSchema = () => {
    return stateIdentificationType === 'saIdNumber'
      ? validationSchemaWithSaIdNumber
      : validationSchemaWithPassportNumber
  }

  const requiredFields = getRequiredFields(getValidationSchema())

  useEffectOnce(() => {
    dispatch(agencyApiEvents.invitations_request())
  })

  /**
   * @todo implement loader
   * @todo close close focus mode on success
   * @todo display errors on error
   */
  const handleOtpSubmit = (otp: { pin: string }): void => {
    const { pin } = otp
    setOtpPin(pin)
    setOtpOpen(false)
    dispatch(goBack())
    if (selectedPartyId === null) {
      dispatch(agencyApiEvents.sendInvite_request({ ...formValues, otp: pin }))
    } else {
      dispatch(
        agencyApiEvents.sendPartyInvite_request({
          params: { partyId: selectedPartyId },
          body: { otp: pin, role: formValues.role },
        }),
      )
    }
  }

  const getPartyFirstName = useSelector(partyApiSelectors.getPartyFirstNameById)
  const getPartyLastName = useSelector(partyApiSelectors.getPartyLastNameById)
  const getPartyEmail = useSelector(partyApiSelectors.getPartyEmailById)
  const getPartyField = useSelector(partyApiSelectors.getPartyPropertyById)
  const agentInvites: $TSFixMe = useSelector(agencyApiSelectors.invitations)

  const handlePartySelect = ({ id }: { id: string }): void => {
    setSelectedPartyId(id)
    // Switch the identification type to match the selected party so the
    // correct field (ID number vs passport) is rendered and autofilled.
    const hasIdNumber = Boolean(getPartyField(id, 'idNumber'))
    setStateIdentificationType(hasIdNumber ? 'saIdNumber' : 'passportNumber')
  }

  const getInitialValues = () => {
    const id = selectedPartyId
    return {
      firstName: getPartyFirstName(id),
      lastName: getPartyLastName(id),
      idNumber: getPartyField(id, 'idNumber'),
      passport: getPartyField(id, 'passport'),
      dateOfBirth: getPartyField(id, 'dateOfBirth'),
      emailAddress: getPartyEmail(id),
      cellNumber: getPartyField(id, 'cellNumber'),
      tellNumber: getPartyField(id, 'telNumber'),
      role: selectedRole,
    }
  }

  return (
    <div>
      <Formik
        validationSchema={getValidationSchema()}
        enableReinitialize={true}
        initialValues={getInitialValues()}
        onSubmit={(values, props) => {
          /**
           * @todo This should be handled on the backend, to be replaced with generic
           * solution when backend returns more detailed exception message
           */
          const existingInviteForEmail = find(
            (invite: { emailAddress: string }) => invite.emailAddress === values.emailAddress,
          )(agentInvites)

          if (existingInviteForEmail) {
            dispatch(agencyApiEvents.existingInviteNotification())
          } else {
            setFormValues(values)
            setOtpOpen(true)
          }
        }}
      >
        {({
          values,
          handleBlur,
          handleChange,
          setFieldValue,
          errors,
          touched,
          submitCount,
          handleSubmit,
          resetForm,
        }) => {
          const handleReset = (): void => {
            resetForm()
            setSelectedPartyId(null)
            setStateIdentificationType('saIdNumber')
            setSelectedRole(userRoles.TeamMember)
          }

          const isMissingRequiredFields = !hasAllRequiredFields(requiredFields, values)

          const submitEvent =
            selectedPartyId === null
              ? agencyApiEvents.sendInvite_request({ ...formValues, otp: otpPin ?? '' })
              : agencyApiEvents.sendPartyInvite_request({
                  params: { partyId: selectedPartyId },
                  body: { otp: otpPin ?? '' },
                })
          const generalErrors = getGeneralFormErrorsByEvent(submitEvent)
          const fieldErrors = getFormFieldErrorsByEvent(submitEvent)
          const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

          const formLoaderState = isSubmitting ? 'submitting' : !isEmpty(errors) || hasApiErrors ? 'error' : undefined

          const handleStateIdentificationType = (identificationType: string) => {
            setStateIdentificationType(identificationType)
          }

          return (
            <div className={styles.root}>
              <FormLoader
                onSubmit={handleSubmit}
                state={formLoaderState}
                buttonProps={{ children: 'Send invite', disabled: isMissingRequiredFields }}
                successText="Invite sent successfully"
                ButtonContainer={createButtonContainer(selectedPartyId, handleReset)}
              >
                <FormErrors errors={generalErrors} />
                <div className={styles['required-fields-info']}>
                  Fields marked with <span>*</span> are required.
                </div>
                <Heading as="h3" size="a5" spacing="small">
                  User role
                </Heading>
                <RadioGroupCards
                  name="role"
                  defaultSelected={values.role}
                  onChange={(e: $TSFixMe) => {
                    setSelectedRole(e.target.value)
                    setFieldValue('role', e.target.value)
                  }}
                >
                  <Radio
                    value="Owner"
                    label="Account owner"
                    labelDescription="Full access, including payment approvals, settings, and user management."
                  />
                  <Radio
                    value="TeamMember"
                    label="Team member"
                    labelDescription="Day-to-day access for managing leases, invoices and contacts."
                  />
                  <Radio
                    value="ReadOnlyMember"
                    label="Read-only"
                    labelDescription="View and download access only. Cannot make changes."
                  />
                </RadioGroupCards>

                <InviteUserDetails
                  requiredFields={requiredFields}
                  values={values}
                  tags={''}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  handleSelect={handlePartySelect}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  submitCount={submitCount}
                  disableFields={selectedPartyId}
                  allowPartySearch={selectedPartyId === null}
                  handleStateIdentificationType={handleStateIdentificationType}
                  stateIdentificationType={stateIdentificationType}
                />
              </FormLoader>

              <StatefulOtpVerification
                isOpen={otpOpen}
                onClose={() => setOtpOpen(false)}
                onSubmit={({ otp }) => handleOtpSubmit(otp)}
                inviteeEmail={formValues?.emailAddress}
              />
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

export default InviteUserForm
